import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import { BatchLoader } from 'src/app/Utils/batch-loader.util';
import SrStatusCodesModel from 'src/app/services/srStatusCodes.model';
import { SrStatusCodesService } from 'src/app/services/sr-status-codes.service';

declare var $: any;

@Component({
  selector: 'app-sr-status-codes',
  templateUrl: './sr-status-codes.component.html',
  styleUrls: ['./sr-status-codes.component.scss']
})
export class SrStatusCodesComponent implements OnInit, OnDestroy {
  public srStatusCodes: SrStatusCodesModel[] = [];
  public srStatusCodesList: SrStatusCodesModel[] = [];
  public srStatusCode: SrStatusCodesModel = new SrStatusCodesModel();
  public filteredSrStatusCodes: SrStatusCodesModel[] = [];
  public search: string = '';
  public submitButton: boolean = false;
  public updateButton: boolean = false;
  public statusCodesDoubleClick: boolean = false;
  public NoDataMessage: boolean = false;
  public activePageNo: number;
  public registerForm: FormGroup;
  public submitted = false;
  public enableDisablePageLoader: boolean = false;
  public listLoader = false;
  public isFiltering: boolean = false;
  public noList: boolean = false;
  searchTimeout: any;
  private scrollTimeout: any;
  public formFieldCount: number = 0;
  public showForm: boolean = false;

  private batchLoader: BatchLoader<any>;

  constructor(
    private toastrService: ToastrService,
    public formBuilder: FormBuilder,
    public srStatusCodesService: SrStatusCodesService,
    private locationStrategy: LocationStrategy,
    private previousRouteService: PreviousRouteService
  ) {
    this.srStatusCode = new SrStatusCodesModel();
    this.registerForm = this.formBuilder.group({
      statuscode: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(12)]],
      description: ['', [Validators.maxLength(50)]]
    });
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  public async ngOnInit() {
    try {
      window.scrollTo(0, 0);
      this.enableDisablePageLoader = true;
      this.clear();
      this.search = '';
      this.srStatusCodes = [];
      this.srStatusCodesList = [];
      this.filteredSrStatusCodes = [];
      this.isFiltering = false;
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());

      this.srStatusCodes = await this.srStatusCodesService.getAllSrStatusCodes();

      this.initializeBatchLoader();
      this.batchLoader?.reset();
      await this.batchLoader?.loadNextBatch(true);
      this.enableDisablePageLoader = false;
    } catch (error: any) {
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.enableDisablePageLoader = false;
    }
  }

  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredSrStatusCodes : this.srStatusCodes,
      (updatedList) => {
        this.srStatusCodesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined,
      (loading) => this.listLoader = loading
    );
  }

  get f() {
    return this.registerForm.controls;
  }

  public ngOnDestroy(): void {
    this.srStatusCodesService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }

  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.srStatusCodes.filter(item =>
          item.code?.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query)
        )
      : this.srStatusCodes;
    this.filteredSrStatusCodes = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      this.noList = true;
      this.srStatusCodesList = [];
      return;
    }
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch();
    }, 300);
  }

  public clear() {
    this.registerForm.reset();
    this.showForm = false;
    this.srStatusCode = new SrStatusCodesModel();
    this.statusCodesDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
  }

  public async addUpdateSrStatusCode() {
    this.submitted = true;
    if (this.registerForm.invalid) {
      return;
    }
    const formData = this.registerForm.value;
    const payload: any = {
      code: formData.statuscode,
      description: formData.description
    };

    try {
      this.statusCodesDoubleClick = true;
      if (this.submitButton) {
        await this.srStatusCodesService.create(payload);
        this.toastrService.success('Status Code created successfully', '', {
          timeOut: 2000
        });
      } else {
        await this.srStatusCodesService.updateOneByID(this.srStatusCode.id, payload);
        this.toastrService.success('Status Code updated successfully', '', {
          timeOut: 2000
        });
      }
      await this.ngOnInit();
    } catch (error: any) {
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.statusCodesDoubleClick = false;
    }
  }

  public getSrStatusCodeById(id: string) {
    this.showForm = true;
    this.submitButton = false;
    this.statusCodesDoubleClick = false;
    this.updateButton = true;
    const found = this.srStatusCodesList.find(item => item?.id === id);
    this.srStatusCode = found ? { ...found } : new SrStatusCodesModel();
    if (this.srStatusCode) {
      this.registerForm.patchValue({
        statuscode: this.srStatusCode.code,
        description: this.srStatusCode.description,
      });
    }
  }

  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}

